import React, { useCallback, useEffect, useState, useMemo } from "react";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import styled, { withTheme } from "styled-components";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import moment from "moment";
import { DEFAULT_LOCALE, getLanguages } from "../../../i18n/helpers";
import { DateFormat, TIME_FORMATS } from "../../../modules/constants";
import DateSelect from "../../ui-library/DateAndTime/Date/DateSelect";

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
`;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  &.month-select {
    .react-datepicker__month-container > .react-datepicker__header,
    .react-datepicker__navigation {
      display: none;
    }
    .react-datepicker__month {
      margin: 1rem 0.4rem;
    }
  }
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const yearRegex = new RegExp("^[0-9]{1,4}$");

const minDate = 1900;
const presentYear = new Date().getFullYear();

const EditParticipant = (props) => {
  const { onSubmit, sites, participantDetails, participant, studyId, loading } =
    props;

  const [errors, setErrors] = useState({});

  const [ugnxId, setUgnxId] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [site, setSite] = useState(participant?.siteId || "");
  const [reasonForChange, setreasonForChange] = useState("");
  const [validReasonForChange, setValidReasonForChange] = useState(false);
  const [language, setLanguage] = useState("");
  const [timeFormat, setTimeFormat] = useState("");

  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);
  const [dateOfEnrollment, setDateOfEnrollment] = useState(null);

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const availableLanguages = useMemo(
    () => getLanguages().filter((language) => language.isAvailable),
    []
  );

  useEffect(() => {
    const timeFormat = TIME_FORMATS.find(
      (timeFormatObj) => timeFormatObj.id === participantDetails?.timeFormat
    );

    setUgnxId(participant?.userName);
    setYearOfBirth(participantDetails?.yearOfBirth);
    setLanguage(participantDetails?.language || DEFAULT_LOCALE);
    setSite(participant?.siteId);
    setTimeFormat(timeFormat);
    setDateOfEnrollment(moment(participant?.dateOfEnrollment).toDate());
  }, [participant, participantDetails]);

  const handleYearOfBirthDateChange = (value) => {
    if (value === "" || (value && yearRegex.test(value))) {
      setYearOfBirth(value);
    }
  };

  const validateYearBirth = () => {
    setErrors({
      ...errors,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${presentYear}`,
    });
  };

  const handleDateOfEnrollmentChange = (value) => {
    if (value) {
      validateDateOfEnrollment(value);
      setDateOfEnrollment(value);
    }
  };

  const validateDateOfEnrollment = (value) => {
    setErrors({
      ...errors,
      dateOfEnrollment: isDateOfEnrollmentValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validateReason = () => {
    let reasonError;

    if (reasonForChange.replace(/\.|,/g, "").trim() === "") {
      reasonError = "Please provide a reason";
    } else if (reasonForChange.length > 450) {
      reasonError = "Please enter a reason with less than 450 characters";
    }
    setErrors({
      ...errors,
      reason: reasonError,
    });
  };

  const isYearBirthValid = useCallback(() => {
    if (!yearOfBirth || yearOfBirth < minDate || yearOfBirth > presentYear) {
      return false;
    }
    return true;
  }, [yearOfBirth]);

  const isDateOfEnrollmentValid = useCallback((value) => {
    return !!value;
  }, []);

  const handleSiteOnChange = (site) => {
    const siteFound = sites?.find((siteObj) => siteObj.name === site.value);
    setSite(siteFound.key);
  };

  const getSites = useMemo(
    () =>
      sites?.map((site) => ({
        id: site.key,
        value: site.name,
      })),
    [sites]
  );

  useEffect(() => {
    setValidReasonForChange(
      reasonForChange.replace(/\.|,/g, "").trim() !== "" &&
        reasonForChange.length <= 450
    );
  }, [reasonForChange]);

  const getChosenSite = useMemo(
    () => sites?.find((siteObj) => siteObj.key === site),
    [site, sites]
  );

  const checkErrors = useCallback(() => {
    setErrors((prevState) => ({
      ...prevState,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${new Date().getFullYear()}`,
    }));
  }, [isYearBirthValid]);

  const validateBeforeSubmit = (event) => {
    event.preventDefault();
    checkErrors();
    if (isYearBirthValid() && reasonForChange !== "") {
      submitForm();
    }
  };

  const getYearOfBirth = useCallback(() => {
    if (isYearBirthValid()) {
      return `${moment(yearOfBirth).get("year")}`;
    }

    return yearOfBirth;
  }, [isYearBirthValid, yearOfBirth]);

  const submitForm = useCallback(() => {
    const { participantId, studyStatusId } = participant;

    const formData = {
      participantId,
      newSiteId: site,
      studyId,
      studyStatus: studyStatusId,
      ugnxId,
      yearOfBirth: parseInt(getYearOfBirth()),
      reasonForChange,
      oldSiteId: participant.siteId,
      language,
      timeFormat: timeFormat.id,
      dateOfEnrollment: moment(dateOfEnrollment).format(
        DateFormat.EXTENDED_DATETIME
      ),
    };

    onSubmit(formData);
  }, [
    onSubmit,
    ugnxId,
    studyId,
    participant,
    reasonForChange,
    getYearOfBirth,
    site,
    language,
    timeFormat,
    dateOfEnrollment,
  ]);

  return (
    <FormContainer data-testid="editparticipant">
      <LabelledInput
        data-testid="ugnxID-input"
        title="Participant ID"
        value={ugnxId}
        type="text"
        placeholder="XXX-XXX"
        disabled
      />
      <LabelledInput
        data-testid="yearOfBirth-input"
        className="mt-4"
        title="Year of birth"
        min={minDate}
        max={presentYear}
        value={getYearOfBirth() || ""}
        type="text"
        placeholder="YYYY"
        pattern="[0-9]*"
        hasError={!!errors?.yearOfBirth}
        errorMessage={errors?.yearOfBirth}
        onChange={(e) => {
          e.target.validity.valid &&
            handleYearOfBirthDateChange(e.target.value);
        }}
        onBlur={() => validateYearBirth()}
        autoFocus={true}
      />

      <div>
        <DatePickerContainer className="mt-4">
          <StyledLabel className="mb-0 me-2">Date of Consent</StyledLabel>
          <DateSelect
            eventDate={dateOfEnrollment}
            setEventDate={handleDateOfEnrollmentChange}
            displayTitle={false}
            errorHandling={errorHandling}
            maxDate={new Date()}
            showMonthDropdown
            showYearDropdown
          />
        </DatePickerContainer>
        <StyledError>
          {errors?.dateOfEnrollment && <span>{errors?.dateOfEnrollment}</span>}
        </StyledError>
      </div>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 me-2">Site</StyledLabel>
        <CustomDropdown
          data-testid="site-dropdown"
          placeholder="Select"
          value={getChosenSite?.name}
          options={getSites}
          limitheight
          onChange={(e) => handleSiteOnChange(e)}
          width={145}
        />
      </StyledContainerHorizontal>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 me-2">Time format</StyledLabel>
        <CustomDropdown
          data-testid="timeformat-dropdown"
          dropdown-data-testid="timeformat-options-dropdown"
          tabindexprop={7}
          value={timeFormat?.value}
          options={TIME_FORMATS}
          width={145}
          onChange={(timeFormat) => setTimeFormat(timeFormat)}
        />
      </StyledContainerHorizontal>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 me-2">Select language</StyledLabel>
        <CustomDropdown
          data-testid="language-dropdown"
          placeholder="Select"
          value={getLanguages().find((lang) => lang.code === language)?.value}
          options={availableLanguages}
          width={145}
          onChange={(lang) => setLanguage(lang.code)}
        />
      </StyledContainerHorizontal>

      <LabelledInput
        title="Reason for change"
        className="mt-4"
        type="text"
        data-testid="update-reason-input"
        hasError={!!errors.reason}
        errorMessage={errors.reason}
        value={reasonForChange}
        onChange={(e) => setreasonForChange(e.target.value)}
        onBlur={() => validateReason()}
      />
      <StyledFooter>
        <PrimaryButton
          data-testid="submitparticipant"
          onClick={(e) => validateBeforeSubmit(e)}
          disabled={!validReasonForChange || loading}
        >
          Save
        </PrimaryButton>
      </StyledFooter>
    </FormContainer>
  );
};

export default withTheme(EditParticipant);
